import {
    operationProgressLabel,
    operationTitle,
    preferredTrackedOperation,
    useKnowledgeOperationProgress,
} from '@/contexts/KnowledgeOperationProgressContext';
import { useLegacyRetrainLearning } from '@/contexts/RetrainProgressContext';
import {
    progressFromRun,
    useWebsiteCrawlProgressState,
} from '@/contexts/WebsiteCrawlProgressContext';
import { type KnowledgeOperation, phaseCountsForOperation } from '@/domain/knowledgeOperations';

export interface KnowledgeLearning {
    /** Active-operation state for the selected domain is still being restored. */
    resolving: boolean;
    /** Rose is reading the site or refreshing its knowledge, for any reason. */
    learning: boolean;
    /** What is running, e.g. "Updating website knowledge" or "Retraining agent". */
    title: string | null;
    /** Where it is in that job, e.g. "Step 1/8 · Discovering website pages". */
    label: string | null;
    /** What the current step does for the client, e.g. "It reads each page you selected." */
    detail: string | null;
    percent: number | null;
    processed: number | null;
    total: number | null;
    /** Items of the current step that gave up, so a stuck step doesn't read as a slow one. */
    failed: number | null;
    /** What the counter counts: the loader ingests documents (a page, a FAQ, an
     *  uploaded file), the website crawl walks pages. */
    unit: 'documents' | 'pages';
    /** The job is the Day-1 website crawl, so "add your own sources" applies. */
    isWebsiteCrawl: boolean;
}

const IDLE: KnowledgeLearning = {
    resolving: false,
    learning: false,
    title: null,
    label: null,
    detail: null,
    percent: null,
    processed: null,
    total: null,
    failed: null,
    unit: 'documents',
    isWebsiteCrawl: false,
};

// One line per step saying what it does for the client; the label under the bar
// already names the step and its count, so this must not repeat it (IX-5327).
const LISTING_PAGES = 'It lists the pages on your website.';
const PICKING_PAGES = 'It picks the pages worth learning from.';
const STEP_DETAILS: Record<string, string> = {
    map: LISTING_PAGES,
    triage: LISTING_PAGES,
    finalize_discovery: LISTING_PAGES,
    sample: PICKING_PAGES,
    decide_section: PICKING_PAGES,
    scrape: 'It reads each page you selected.',
    clean: 'It keeps the useful text and drops menus, footers and banners.',
    document_loader_job: 'It adds the new content to what it answers from.',
};
const REMOVING_OUTDATED = 'It removes outdated content so answers stay current.';
const LOADER_DETAILS: Record<string, string> = {
    replacing_documents: REMOVING_OUTDATED,
    removing_documents: REMOVING_OUTDATED,
    verifying_searchable: 'It checks the new content is ready to answer from.',
};
const RETRAIN_DETAIL = 'It updates what it answers from.';
const STARTING_DETAIL = 'It will start in a moment.';

const INGESTION_PROGRESS_SIMULATION_PARAM = 'simulateIngestionProgress';
const INGESTION_PROGRESS_SIMULATION_HOSTS = new Set([
    'localhost',
    '127.0.0.1',
    'apptest.userose.ai',
]);

/**
 * Read-only visual-QA override for the ingestion surfaces. Keep this off the
 * production host: it deliberately drives the same Playground gate and sidebar
 * signal as a real website crawl, but never starts or mutates an ingestion job.
 */
export function getSimulatedKnowledgeLearning(
    hostname: string,
    search: string
): KnowledgeLearning | null {
    if (!INGESTION_PROGRESS_SIMULATION_HOSTS.has(hostname)) return null;

    const rawPercent = new URLSearchParams(search).get(INGESTION_PROGRESS_SIMULATION_PARAM);
    if (rawPercent == null || !/^(?:0|[1-9]\d?)$/.test(rawPercent)) return null;

    const percent = Number(rawPercent);
    return {
        resolving: false,
        learning: true,
        title: 'Updating website knowledge',
        label: 'Fetching website content',
        detail: STEP_DETAILS.scrape,
        percent,
        processed: percent,
        total: 100,
        failed: null,
        unit: 'pages',
        isWebsiteCrawl: true,
    };
}

/**
/**
 * Highest loader percent already shown per operation, so a loader retry cannot
 * rewind the bar: the work item restarts at 0 documents when it retries, which
 * would drop a bar sitting at 96% back to 0. Module-level rather than a ref so
 * every consumer of this hook agrees on the number.
 */
const highWaterMarks = new Map<string, number>();

// Loader phases that report no count of their own: two serial delete legs and the
// wait for new content to become searchable. The document counter is frozen while
// they run, so a bar pinned at 99% and "32/32" read as a stalled job; pulse and
// drop the count instead (IX-5286).
const UNMEASURED_LOADER_PHASES = new Set([
    'replacing_documents',
    'removing_documents',
    'verifying_searchable',
]);

function isUnmeasuredLoaderPhase(operation: KnowledgeOperation): boolean {
    return operation.loader_phase != null && UNMEASURED_LOADER_PHASES.has(operation.loader_phase);
}

// Page-level steps of a website refresh: one work item per page, so their own
// counters measure the step while `progress_percent` barely moves (IX-5327).
const PAGE_STEPS = new Set(['scrape', 'clean']);

interface StepCount {
    processed: number;
    total: number;
    failed: number;
}

function pageStepCount(operation: KnowledgeOperation): StepCount | null {
    const phase = operation.current_phase;
    if (!phase || !PAGE_STEPS.has(phase)) return null;
    const counts = phaseCountsForOperation(operation, phase);
    if (!counts?.total) return null;
    // A skipped page is done too (unchanged content); counting only successes
    // would leave the bar short of full on a step that finished.
    return {
        processed: counts.succeeded + counts.skipped,
        total: counts.total,
        failed: counts.failed,
    };
}

/**
 * Percent for the bar, measuring the current step rather than the pipeline.
 *
 * The label names the step ("Step 3/3 · …"), so the bar fills that step's own
 * counter and restarts at the next one (IX-5327): the page steps from their work
 * items, the loader from its documents. A pipeline-wide number beside a step
 * counter reads as a contradiction (27% beside 2/72 documents). The loader is a
 * single work item (IX-4710), so its documents are the only thing that moves
 * while it runs. The 99 cap leaves "finished" to the server.
 *
 * Null means indeterminate: nothing to count yet, or nothing to count at all — a
 * deletion whose only work is orphan cleanup — where a bar pinned at a number
 * reads as a stalled job. Zero documents done out of a known total is honest
 * progress, not an unknown, and stays a real 0%.
 */
function operationPercent(operation: KnowledgeOperation): number | null {
    const { loader_processed: processed, loader_total: total } = operation;
    if (isUnmeasuredLoaderPhase(operation)) return null;
    // A scan is one map work item with nothing to count until it settles, so
    // any number would be a guess; pulse instead (IX-5230).
    if (operation.operation_type === 'website_rescan') return null;
    // The bar tracks the current page step and restarts at the next one.
    const step = pageStepCount(operation);
    if (step) return Math.min(99, Math.round((step.processed / step.total) * 100));
    if (operation.current_phase !== 'document_loader_job') return operation.progress_percent;
    if (!total) return null;
    const percent = Math.min(99, Math.round((100 * (processed ?? 0)) / total));
    const seen = Math.max(percent, highWaterMarks.get(operation.id) ?? 0);
    highWaterMarks.set(operation.id, seen);
    return seen;
}

const WEBSITE_OPERATION_TYPES = new Set([
    'website_rescan',
    'website_full_refresh',
    'website_selected_refresh',
    'website_policy_change',
]);

function operationDetail(operation: KnowledgeOperation): string {
    const loaderDetail = operation.loader_phase && LOADER_DETAILS[operation.loader_phase];
    if (loaderDetail) return loaderDetail;
    const phase = operation.current_phase;
    if (!phase) return STARTING_DETAIL;
    // A documents/FAQ retrain runs the loader alone over content it already has.
    if (phase === 'document_loader_job' && !WEBSITE_OPERATION_TYPES.has(operation.operation_type))
        return RETRAIN_DETAIL;
    return STEP_DETAILS[phase] ?? RETRAIN_DETAIL;
}

/**
 * One "is Rose learning right now?" signal for the whole app, whichever side
 * owns the progress: a durable knowledge operation or the legacy website crawl.
 * With several operations in flight the newest wins, matching how the provider
 * resolves scope ownership.
 */
export function useKnowledgeLearning(): KnowledgeLearning {
    const { activeEntries, isRestoring } = useKnowledgeOperationProgress();
    const { activeRun } = useWebsiteCrawlProgressState();
    const legacy = useLegacyRetrainLearning();

    const simulation =
        typeof window === 'undefined'
            ? null
            : getSimulatedKnowledgeLearning(window.location.hostname, window.location.search);
    if (simulation) return simulation;

    const entry = preferredTrackedOperation(activeEntries);
    if (entry) {
        const operation = entry.operation;
        const type = operation?.operation_type ?? entry.hint.operationType;
        const step = operation ? pageStepCount(operation) : null;
        const counted = operation != null && !isUnmeasuredLoaderPhase(operation);
        return {
            resolving: isRestoring,
            learning: true,
            title: operationTitle(entry),
            label: operationProgressLabel(entry),
            detail: operation ? operationDetail(operation) : STARTING_DETAIL,
            percent: operation ? operationPercent(operation) : null,
            processed: step ? step.processed : counted ? operation.loader_processed : null,
            total: step ? step.total : counted ? operation.loader_total : null,
            failed: step?.failed || null,
            unit: step ? 'pages' : 'documents',
            isWebsiteCrawl: WEBSITE_OPERATION_TYPES.has(type),
        };
    }

    if (activeRun) {
        const { label, ...progress } = progressFromRun(activeRun);
        return {
            resolving: isRestoring,
            learning: true,
            title: 'Reading your website',
            label,
            detail: 'It picks the useful pages on your website.',
            ...progress,
            failed: null,
            unit: 'pages',
            isWebsiteCrawl: true,
        };
    }

    // A documents/FAQ retrain that never became a knowledge operation. It has no
    // surface of its own (IX-4724), so it reports here or it is invisible once
    // the client leaves the Knowledge screen that started it.
    if (legacy) {
        const { processed, total } = legacy;
        return {
            resolving: isRestoring,
            learning: true,
            title: legacy.title,
            label: legacy.label,
            detail: RETRAIN_DETAIL,
            percent: total ? Math.min(99, Math.round(((processed ?? 0) / total) * 100)) : null,
            processed,
            total,
            failed: null,
            unit: 'documents',
            isWebsiteCrawl: false,
        };
    }

    return isRestoring ? { ...IDLE, resolving: true } : IDLE;
}
